import { IParamProps } from '@ucheba/ui/components/Param/types'
import { useMemo } from 'react'
import { getCountForm } from '@ucheba/utils/helpers/number'
import { IProfCardStatsProps } from './types'

interface IUseProfCardStats {
  (props: IProfCardStatsProps): {
    institutions: IParamProps | null
    collages: IParamProps | null
    specialities: IParamProps | null
    courses: IParamProps | null
    hrefInstitutions: string
    hrefCollages: string
    hrefCourses: string
  }
}

export const useProfCardStats: IUseProfCardStats = (props) => {
  const {
    collagesCount,
    institutionsCount,
    specialitiesCount,
    coursesCount,
    tagIds,
    specialityIds,
  } = props

  const institutions = useMemo(() => {
    if (institutionsCount === 0) return null
    if (typeof institutionsCount === 'number') {
      return {
        entity: institutionsCount,
        clarification: getCountForm(institutionsCount, ['вуз', 'вуза', 'вузов']),
      }
    }

    return null
  }, [institutionsCount])

  const collages = useMemo(() => {
    if (collagesCount === 0) return null
    if (typeof collagesCount === 'number') {
      return {
        entity: collagesCount,
        clarification: getCountForm(collagesCount, ['колледж', 'колледжа', 'колледжей']),
      }
    }

    return null
  }, [collagesCount])

  const specialities = useMemo(() => {
    if (specialitiesCount === 0) return null
    if (typeof specialitiesCount === 'number') {
      return {
        entity: specialitiesCount,
        clarification: getCountForm(specialitiesCount, [
          'специальность',
          'специальности',
          'специальностей',
        ]),
      }
    }

    return null
  }, [specialitiesCount])

  const courses = useMemo(() => {
    if (coursesCount === 0) return null
    if (typeof coursesCount === 'number') {
      return {
        entity: coursesCount,
        clarification: getCountForm(coursesCount, ['курс', 'курса', 'курсов']),
      }
    }

    return null
  }, [coursesCount])

  const hrefInstitutions = useMemo(() => {
    if (!specialityIds) return `/for-abiturients/vuz`

    const specialityIdsStr = specialityIds
      ?.map((id, key) => {
        const paramName = encodeURIComponent(`eq[${key}]`)
        const paramValue = encodeURIComponent(`__sp:${id}`)

        return `${paramName}=${paramValue}`
      })
      .join('&')
    return `/for-abiturients/vuz?${specialityIdsStr}`
  }, [specialityIds])

  const hrefCollages = useMemo(() => {
    if (!specialityIds) return `/for-abiturients/college`

    const specialityIdsStr = specialityIds
      ?.map((id, key) => {
        const paramName = encodeURIComponent(`eq[${key}]`)
        const paramValue = encodeURIComponent(`__sp:${id}`)
        return `${paramName}=${paramValue}`
      })
      .join('&')

    return `/for-abiturients/college?${specialityIdsStr}`
  }, [specialityIds])

  const hrefCourses = useMemo(() => {
    if (!tagIds) return `/for-specialists/courses`

    const tagIdsStr = tagIds
      ?.map((id, key) => {
        const paramName = encodeURIComponent(`eq[${key}]`)
        const paramValue = encodeURIComponent(`__t:${id}`)

        return `${paramName}=${paramValue}`
      })
      .join('&')

    return `/for-specialists/courses?${tagIdsStr}`
  }, [tagIds])

  return {
    institutions,
    collages,
    specialities,
    courses,
    hrefInstitutions,
    hrefCollages,
    hrefCourses,
  }
}
